/* ===== Finance Page ===== */

import { useEffect, useState } from 'react'
import type { ExpenseRecord, IncomeRecord } from '@zetyar/domain'
import { Check, DollarSign, Plus, Trash2, TrendingDown, TrendingUp, X } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webFinanceRepository } from '@/infrastructure/repositories/finance'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

const EXPENSE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
}

const EXPENSE_STATUS_VARIANT: Record<string, 'neutral' | 'success' | 'danger'> = {
  pending: 'neutral',
  approved: 'success',
  rejected: 'danger',
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
}

type ModalKind = 'income' | 'expense' | null

export function FinancePage() {
  const { currentMembership, currentOrganization, profile, user, permissions } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const canDeleteIncome = profile?.isSuperAdmin || permissions.includes('finance.income.edit')
  const canDeleteExpense = profile?.isSuperAdmin || permissions.includes('finance.expense.create')
  const canApproveExpense = profile?.isSuperAdmin || permissions.includes('finance.expense.approve')
  const [income, setIncome] = useState<IncomeRecord[]>([])
  const [expenses, setExpenses] = useState<ExpenseRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [modal, setModal] = useState<ModalKind>(null)
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const loadOverview = async () => {
    const context = tenantContext()
    if (!context) {
      setIncome([])
      setExpenses([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = await webFinanceRepository.listOverview(context, { limit: 100 })
      setIncome(result.income)
      setExpenses(result.expenses)
    } catch (error) {
      console.error('Error al cargar finanzas:', error)
      setIncome([])
      setExpenses([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      if (!isMounted) return
      await loadOverview()
    }

    void run()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const totalIncome = income.reduce((sum, item) => sum + item.amount, 0)
  const totalExpenses = expenses
    .filter((item) => item.status === 'approved')
    .reduce((sum, item) => sum + item.amount, 0)
  const balance = totalIncome - totalExpenses

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const context = tenantContext()
    if (!context || !modal) return

    const amountValue = Number(amount)
    if (!Number.isFinite(amountValue) || amountValue <= 0) return

    try {
      if (modal === 'income' && webFinanceRepository.createIncome) {
        await webFinanceRepository.createIncome(context, { amount: amountValue, description, date })
      } else if (modal === 'expense' && webFinanceRepository.createExpense) {
        await webFinanceRepository.createExpense(context, { amount: amountValue, description, date })
      }
      await loadOverview()
    } catch (error) {
      console.error('Error al registrar el movimiento en Supabase:', error)
    }

    setModal(null)
    setAmount('')
    setDescription('')
  }

  const handleDecision = (expenseId: string, createdByMembershipId: string, decision: 'approved' | 'rejected') => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webFinanceRepository.decideExpense) return

      try {
        await webFinanceRepository.decideExpense(context, expenseId, createdByMembershipId, decision)
        await loadOverview()
      } catch (error) {
        console.error('Error al decidir sobre el egreso:', error)
      }
    })
  }

  const handleDeleteIncome = (incomeId: string) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webFinanceRepository.deleteIncome) return

      try {
        await webFinanceRepository.deleteIncome(context, incomeId)
        await loadOverview()
      } catch (error) {
        console.error('Error al eliminar el ingreso:', error)
      }
    })
  }

  const handleDeleteExpense = (expenseId: string) => {
    guard(async () => {
      const context = tenantContext()
      if (!context || !webFinanceRepository.deleteExpense) return

      try {
        await webFinanceRepository.deleteExpense(context, expenseId)
        await loadOverview()
      } catch (error) {
        console.error('Error al eliminar el egreso:', error)
      }
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Finanzas</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Ingresos, egresos y balance de {currentOrganization?.name}
          </p>
        </div>
        <div className="flex gap-2">
          {(profile?.isSuperAdmin || permissions.includes('finance.income.create')) && (
            <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setModal('income')}>
              Registrar ingreso
            </Button>
          )}
          {(profile?.isSuperAdmin || permissions.includes('finance.expense.create')) && (
            <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setModal('expense')}>
              Registrar egreso
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 bg-success-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Ingresos</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatAmount(totalIncome, 'CLP')}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 bg-danger-50 dark:bg-red-950/40 text-danger-600 dark:text-red-300">
            <TrendingDown className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Egresos aprobados</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatAmount(totalExpenses, 'CLP')}</p>
          </div>
        </Card>
        <Card className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md flex items-center justify-center shrink-0 bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-200">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">Balance</p>
            <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{formatAmount(balance, 'CLP')}</p>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Egresos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
          ) : expenses.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay egresos registrados.</p>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {expenses.map((expense) => (
                <div key={expense.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{expense.description}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {new Date(expense.date).toLocaleDateString('es-CL')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {formatAmount(expense.amount, expense.currency)}
                    </span>
                    <Badge variant={EXPENSE_STATUS_VARIANT[expense.status]}>
                      {EXPENSE_STATUS_LABEL[expense.status]}
                    </Badge>
                    {expense.status === 'pending' && canApproveExpense && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<Check className="w-3.5 h-3.5 text-emerald-600" />}
                          onClick={() => handleDecision(expense.id, expense.createdBy, 'approved')}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<X className="w-3.5 h-3.5 text-danger-600" />}
                          onClick={() => handleDecision(expense.id, expense.createdBy, 'rejected')}
                        >
                          Rechazar
                        </Button>
                      </>
                    )}
                    {canDeleteExpense && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        aria-label="Eliminar egreso"
                        onClick={() => handleDeleteExpense(expense.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-danger-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ingresos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
          ) : income.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No hay ingresos registrados.</p>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {income.map((item) => (
                <div key={item.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{item.description}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      {new Date(item.date).toLocaleDateString('es-CL')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">
                      {formatAmount(item.amount, item.currency)}
                    </span>
                    {canDeleteIncome && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 flex items-center justify-center"
                        aria-label="Eliminar ingreso"
                        onClick={() => handleDeleteIncome(item.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-danger-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'income' ? 'Registrar ingreso' : 'Registrar egreso'}
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Monto (CLP)"
            type="number"
            min="1"
            step="1"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Input
            label="Descripcion"
            placeholder="Ej: Cuota mensual, arriendo de sala..."
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label="Fecha"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          {modal === 'expense' && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              El egreso queda pendiente de aprobacion. Quien lo registra no puede aprobarlo.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800">
            <Button variant="outline" type="button" onClick={() => setModal(null)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar
            </Button>
          </div>
        </form>
      </Modal>
      {reauthModal}
    </div>
  )
}
